import { useState } from 'react';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/auth/authContext';
import { routePaths } from '@/app/router/routePaths';
import { isDevSignInEnabled } from '@/infrastructure/firebase/authService';
import { Button } from '@/shared/ui/Button';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';

import { GoogleIcon } from './GoogleIcon';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  usePageTitle('Anmelden');
  const { session, error, signIn, dismissError } = useAuth();
  const location = useLocation();
  const [isSigningIn, setIsSigningIn] = useState(false);

  if (session.status === 'loading') {
    return <LoadingScreen label="Anmeldung wird geprüft …" />;
  }

  if (session.status === 'authenticated') {
    const target = (location.state as LocationState | null)?.from ?? routePaths.team.profile;
    return <Navigate to={target} replace />;
  }

  const devMode = isDevSignInEnabled();

  const handleSignIn = () => {
    setIsSigningIn(true);
    void signIn().finally(() => {
      setIsSigningIn(false);
    });
  };

  return (
    <div className="flex min-h-[70dvh] items-center justify-center px-4">
      <div className="border-line bg-surface w-full max-w-md rounded-xl border p-8">
        <p className="text-brand mb-2 text-xs font-semibold tracking-widest uppercase">
          Freiwillige Feuerwehr Ratekau
        </p>
        <h1 className="mb-2 text-2xl font-bold tracking-tight">Feuerwehr-Dart-Cup</h1>
        <p className="text-fg-muted mb-8 text-sm">
          Als Teamkapitän anmelden, um das Team zu verwalten und Ergebnisse zu melden.
        </p>

        {error !== null && (
          <div
            role="alert"
            className="bg-danger-subtle border-danger/40 text-fg mb-6 rounded-lg border p-4 text-sm"
          >
            <p>{error.message}</p>
            <button
              type="button"
              onClick={dismissError}
              className="text-fg-muted hover:text-fg mt-2 text-xs underline"
            >
              Ausblenden
            </button>
          </div>
        )}

        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          isLoading={isSigningIn}
          icon={devMode ? undefined : <GoogleIcon />}
          onClick={handleSignIn}
        >
          {devMode ? 'Anmelden (Entwicklungsmodus)' : 'Mit Google anmelden'}
        </Button>

        {devMode ? (
          <p className="text-warning mt-6 text-xs leading-relaxed">
            Entwicklungsmodus: Ein Klick meldet ein Test-Konto mit Turnierleitungsrechten an. Der
            echte Google-Login ist nur im Produktionsbuild aktiv.
          </p>
        ) : (
          <p className="text-fg-subtle mt-6 text-xs leading-relaxed">
            Die Anmeldung ist ausschließlich über Google möglich. Der Zugang zur Turnierleitung wird
            nicht über diese Seite vergeben.
          </p>
        )}
      </div>
    </div>
  );
}
